import type { ProductFeatureGroup } from "@itaas/pep-contracts";
import type {
  ProductAudienceItem,
  ProductDetailHdConfig,
  ProductOutcomeItem,
  ProductTrustChip,
} from "./product-detail-types";

export const HOSPITOL_HERO_PRODUCT_ASSET =
  "/assets/products/hospitol/hero/itaas-hospital-product-dashboard.png";
export const HOSPITOL_HERO_HUMAN_ASSET =
  "/assets/products/hospitol/hero/itaas-hospital-medical-team.png?v=1";

/** Catalog SoT — pharmacy / care path first; no full HIS / durable DPI GA claims. */
export const HOSPITOL_AUDIENCES: ProductAudienceItem[] = [
  {
    id: "direction",
    icon: "🏥",
    titleFr: "Direction hospitalière",
    titleEn: "Hospital leadership",
    bodyFr: "Pilotez accueil, consultations et caisse dans un fil patient unique.",
    bodyEn: "Steer reception, consultations and billing in one patient thread.",
  },
  {
    id: "clinicians",
    icon: "🩺",
    titleFr: "Médecins & infirmiers",
    titleEn: "Doctors & nurses",
    bodyFr: "Coordonnez les soins autour du parcours patient — Care / Clinic / Full.",
    bodyEn: "Coordinate care around the patient journey — Care / Clinic / Full.",
  },
  {
    id: "pharmacy",
    icon: "💊",
    titleFr: "Pharmacie hospitalière",
    titleEn: "Hospital pharmacy",
    bodyFr: "Ordonnances, stock médical et Print Bridge composés avec ITaaS Inventory.",
    bodyEn: "Prescriptions, medical stock and Print Bridge composed with ITaaS Inventory.",
  },
  {
    id: "finance",
    icon: "💳",
    titleFr: "Caisse & mutuelles",
    titleEn: "Billing & insurers",
    bodyFr: "Mobile Money, espèces et hub assurance sans bloquer les soins.",
    bodyEn: "Mobile Money, cash and insurer hub without blocking care.",
  },
  {
    id: "ops",
    icon: "🗂️",
    titleFr: "Opérations cliniques",
    titleEn: "Clinical operations",
    bodyFr: "Accueil, consultations, pharmacie et labo reliés — déploiement progressif.",
    bodyEn: "Reception, consultations, pharmacy and lab connected — progressive rollout.",
  },
];

export const HOSPITOL_OUTCOMES: ProductOutcomeItem[] = [
  {
    id: "thread",
    icon: "🧵",
    titleFr: "Un fil patient continu",
    titleEn: "One continuous patient thread",
    bodyFr: "De l’accueil à la caisse : où est le patient, qui le soigne, qui paie ?",
    bodyEn: "From reception to billing: where is the patient, who treats them, who pays?",
  },
  {
    id: "pharmacy",
    icon: "📦",
    titleFr: "Pharmacie composée",
    titleEn: "Composed pharmacy",
    bodyFr: "Stock médical et étiquettes via Inventory + Print Bridge.",
    bodyEn: "Medical stock and labels via Inventory + Print Bridge.",
  },
  {
    id: "cash",
    icon: "⚡",
    titleFr: "Caisse temps réel",
    titleEn: "Real-time cash desk",
    bodyFr: "Mobile Money et trésorerie alignés sur le parcours de soins.",
    bodyEn: "Mobile Money and treasury aligned with the care journey.",
  },
  {
    id: "rollout",
    icon: "🚀",
    titleFr: "Déploiement en 2 semaines",
    titleEn: "Two-week rollout",
    bodyFr: "Accueil + caisse + consultation d’abord, puis pharmacie et mutuelle.",
    bodyEn: "Reception + billing + consultation first, then pharmacy and insurers.",
  },
];

export const HOSPITOL_CAPABILITY_GROUPS: ProductFeatureGroup[] = [
  {
    titleFr: "Parcours patient",
    titleEn: "Patient journey",
    features: [
      "Accueil et consultations",
      "Fil patient unique (accueil → caisse)",
      "Éditions Care / Clinic / Full",
    ],
  },
  {
    titleFr: "Pharmacie & stock",
    titleEn: "Pharmacy & stock",
    features: [
      "Pharmacie hospitalière composée avec ITaaS Inventory",
      "Pont d'impression d'étiquettes (Print Bridge)",
      "Vente comptoir et gestion ordonnances",
    ],
  },
  {
    titleFr: "Finances & assurance",
    titleEn: "Finance & insurance",
    features: [
      "Hub assurance et mutuelles sans blocage des soins",
      "Mobile Money, espèces et trésorerie en temps réel",
    ],
  },
  {
    titleFr: "Suite ITaaS",
    titleEn: "ITaaS Suite",
    features: [
      "RH soignants, stock médical et facturation composables",
      "Hébergement cloud, formation et paramétrage inclus",
    ],
  },
];

const HOSPITOL_TRUST_BASE: ProductTrustChip[] = [
  {
    id: "vertical",
    labelFr: "Plateforme hospitalière · Care / Clinic / Full",
    labelEn: "Hospital platform · Care / Clinic / Full",
  },
  {
    id: "pharmacy",
    labelFr: "Pharmacie + Print Bridge + Inventory",
    labelEn: "Pharmacy + Print Bridge + Inventory",
  },
  {
    id: "rdc",
    labelFr: "Conçue pour la réalité congolaise",
    labelEn: "Built for the Congolese reality",
  },
];

export const HOSPITOL_HD_CONFIG: ProductDetailHdConfig = {
  productImageSrc: HOSPITOL_HERO_PRODUCT_ASSET,
  productImageAlt: {
    fr: "Tableau de bord ITaaS Hospitol — opérations hospitalières",
    en: "ITaaS Hospitol dashboard — hospital operations",
  },
  humanImageSrc: HOSPITOL_HERO_HUMAN_ASSET,
  humanImageAlt: {
    fr: "Médecin et infirmière consultant une tablette ensemble",
    en: "Doctor and nurse reviewing a tablet together",
  },
  visualCaption: {
    fr: "Interface produit ITaaS Hospitol — plateforme hospitalière",
    en: "ITaaS Hospitol product interface — hospital platform",
  },
  audiences: HOSPITOL_AUDIENCES,
  audienceTitle: {
    fr: "Une plateforme pour chaque acteur de l’établissement",
    en: "A platform for every stakeholder in the facility",
  },
  outcomes: HOSPITOL_OUTCOMES,
  outcomesLead: {
    fr: "Des bénéfices concrets pour fluidifier soins, pharmacie et caisse.",
    en: "Practical benefits to streamline care, pharmacy and billing.",
  },
  capabilityGroups: HOSPITOL_CAPABILITY_GROUPS,
  preferConfigCapabilities: true,
  contextTitle: {
    fr: "Plateforme hospitalière intelligente",
    en: "Intelligent hospital platform",
  },
  contextLead: {
    fr: "Parcours patients, pharmacie et facturation opérationnels — capacités cliniques avancées selon édition, sans affirmation HIS complet GA.",
    en: "Patient journeys, pharmacy and billing operational — advanced clinical capabilities by edition, without a full HIS GA claim.",
  },
  contextFallbackPoints: [
    {
      fr: "Accueil, consultations et caisse (Mobile Money)",
      en: "Reception, consultations and billing (Mobile Money)",
    },
    {
      fr: "Pharmacie hospitalière composée avec Inventory",
      en: "Hospital pharmacy composed with Inventory",
    },
    {
      fr: "Print Bridge et hub mutuelles / assureurs",
      en: "Print Bridge and insurer / mutuals hub",
    },
  ],
  trustBaseChips: HOSPITOL_TRUST_BASE,
  showAmbassadorLink: false,
  showEditionShowcase: true,
  ctaPriority: "demo",
};
